/** Small presentational building blocks shared by every page. */

import { useEffect, type ReactNode, type SelectHTMLAttributes } from 'react'

import type { CompanyMarket, CompanyStatus, Market, RunStatus, SourceStatus } from '@/api/types'
import { Button } from '@/components/ui/Button'
import { CATEGORY_LABEL, COMPANY_STATUS_LABEL, MARKET_FLAG, cn } from '@/lib/utils'

// ---- Badges -----------------------------------------------------------------

type Tone = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'dark'

const TONES: Record<Tone, string> = {
  neutral: 'bg-surface-muted text-ink-soft border-line',
  success: 'bg-success/10 text-[#1f7a4d] border-success/25',
  warning: 'bg-warning/10 text-[#8a5a0f] border-warning/30',
  danger: 'bg-danger/10 text-danger border-danger/25',
  info: 'bg-info/10 text-info border-info/25',
  dark: 'bg-ink text-white border-ink',
}

export function Badge({ tone = 'neutral', children, className }: { tone?: Tone; children: ReactNode; className?: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 whitespace-nowrap rounded-pill border px-2 py-0.5 text-[11.5px] font-medium',
        TONES[tone],
        className,
      )}
    >
      {children}
    </span>
  )
}

const SOURCE_TONE: Record<SourceStatus, Tone> = {
  pending: 'info',
  active: 'success',
  empty: 'warning',
  failing: 'danger',
  paused: 'neutral',
}

export function SourceStatusBadge({ status, crawling }: { status: SourceStatus; crawling?: boolean }) {
  if (crawling) {
    return (
      <Badge tone="dark">
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" /> Crawling
      </Badge>
    )
  }
  const label = { pending: 'Queued', active: 'Active', empty: 'No jobs', failing: 'Failing', paused: 'Paused' }[status]
  return <Badge tone={SOURCE_TONE[status]}>{label}</Badge>
}

const RUN_TONE: Record<RunStatus, Tone> = { running: 'dark', success: 'success', empty: 'warning', failed: 'danger' }

export function RunStatusBadge({ status }: { status: RunStatus }) {
  const label = { running: 'Running', success: 'Success', empty: 'No jobs', failed: 'Failed' }[status]
  return <Badge tone={RUN_TONE[status]}>{label}</Badge>
}

// ---- Form controls ------------------------------------------------------------

export function Select({ className, children, ...props }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select className={cn('field-box h-[38px] cursor-pointer py-0 pr-8', className)} {...props}>
      {children}
    </select>
  )
}

export function Label({ children, hint }: { children: ReactNode; hint?: ReactNode }) {
  return (
    <span className="mb-1.5 flex items-baseline justify-between text-[13px] font-medium text-ink-soft">
      {children}
      {hint && <span className="text-[12px] font-normal text-ink-faint">{hint}</span>}
    </span>
  )
}

export function Segmented<T extends string>({
  value,
  options,
  onChange,
  size = 'md',
}: {
  value: T
  options: Array<{ value: T; label: ReactNode }>
  onChange: (value: T) => void
  size?: 'sm' | 'md'
}) {
  return (
    <div role="radiogroup" className="inline-flex rounded-field border border-line bg-surface-muted p-0.5">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={value === option.value}
          onClick={() => onChange(option.value)}
          className={cn(
            'rounded-[8px] font-medium transition',
            size === 'sm' ? 'px-2.5 py-1 text-[12.5px]' : 'px-3.5 py-1.5 text-[13.5px]',
            value === option.value ? 'bg-surface text-ink shadow-card' : 'text-ink-muted hover:text-ink',
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  )
}

// ---- Feedback -----------------------------------------------------------------

export function EmptyState({ title, body, action }: { title: string; body?: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
      <div className="mb-3 grid h-11 w-11 place-items-center rounded-full bg-surface-muted text-ink-faint">
        <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
          <path d="M3 7h18M3 12h18M3 17h10" strokeLinecap="round" />
        </svg>
      </div>
      <p className="text-[15px] font-medium text-ink">{title}</p>
      {body && <p className="mt-1 max-w-md text-[13.5px] text-ink-muted">{body}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  )
}

export function ErrorBox({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const message = error instanceof Error ? error.message : 'Something went wrong.'
  return (
    <div className="flex items-center justify-between gap-4 rounded-field border border-danger/25 bg-danger/5 px-4 py-3 text-[13.5px] text-danger">
      <span>{message}</span>
      {onRetry && (
        <Button size="sm" variant="secondary" onClick={onRetry}>
          Retry
        </Button>
      )}
    </div>
  )
}

export function SkeletonRows({ rows = 6, cols = 5 }: { rows?: number; cols?: number }) {
  return (
    <>
      {Array.from({ length: rows }, (_, r) => (
        <tr key={r}>
          {Array.from({ length: cols }, (_, c) => (
            <td key={c}>
              <div className="skeleton h-4" style={{ width: `${50 + ((r * 7 + c * 13) % 45)}%` }} />
            </td>
          ))}
        </tr>
      ))}
    </>
  )
}

// ---- Pagination -------------------------------------------------------------------

export function Pagination({
  page,
  pageSize,
  total,
  estimate,
  onPage,
}: {
  page: number
  pageSize: number
  total: number
  estimate?: boolean
  onPage: (page: number) => void
}) {
  const pages = Math.max(1, Math.ceil(total / pageSize))
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1
  const to = Math.min(page * pageSize, total)
  return (
    <div className="flex items-center justify-between gap-3 border-t border-line px-4 py-3 text-[13px] text-ink-muted">
      <span>
        {from.toLocaleString()}–{to.toLocaleString()} of {total.toLocaleString()}
        {estimate ? '+' : ''}
      </span>
      <div className="flex items-center gap-2">
        <Button size="sm" variant="secondary" disabled={page <= 1} onClick={() => onPage(page - 1)}>
          Previous
        </Button>
        <span className="tabular-nums">
          {page} / {pages}
          {estimate ? '+' : ''}
        </span>
        <Button size="sm" variant="secondary" disabled={page >= pages && !estimate} onClick={() => onPage(page + 1)}>
          Next
        </Button>
      </div>
    </div>
  )
}

// ---- Overlays -------------------------------------------------------------------

function useEscape(onClose: () => void) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose])
}

export function Modal({
  title,
  onClose,
  children,
  width = 'max-w-2xl',
}: {
  title: ReactNode
  onClose: () => void
  children: ReactNode
  width?: string
}) {
  useEscape(onClose)
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink/30 p-4 pt-[8vh] backdrop-blur-[2px] animate-fade-in">
      <div className="absolute inset-0" onClick={onClose} aria-hidden />
      <div role="dialog" aria-modal="true" className={cn('card relative w-full animate-fade-up shadow-float', width)}>
        <div className="flex items-center justify-between border-b border-line px-6 py-4">
          <h2 className="text-[17px] font-semibold">{title}</h2>
          <CloseButton onClick={onClose} />
        </div>
        <div className="px-6 py-5">{children}</div>
      </div>
    </div>
  )
}

export function Drawer({ onClose, children }: { onClose: () => void; children: ReactNode }) {
  useEscape(onClose)
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-ink/25 animate-fade-in">
      <div className="absolute inset-0" onClick={onClose} aria-hidden />
      <aside
        role="dialog"
        aria-modal="true"
        className="relative flex h-full w-full max-w-[720px] flex-col overflow-hidden bg-surface shadow-float animate-fade-up"
      >
        {children}
      </aside>
    </div>
  )
}

export function CloseButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Close"
      className="grid h-8 w-8 place-items-center rounded-full text-ink-muted hover:bg-surface-muted hover:text-ink"
    >
      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
        <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
      </svg>
    </button>
  )
}

export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
      <div>
        <h1 className="text-[26px] font-semibold tracking-tight">{title}</h1>
        {subtitle && <p className="mt-1 text-[14px] text-ink-muted">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  )
}

// ---- Companies -------------------------------------------------------------------

const COMPANY_TONE: Record<CompanyStatus, Tone> = {
  no_career_page: 'warning',
  pending: 'info',
  active: 'success',
  failing: 'danger',
  paused: 'neutral',
}

export function CompanyStatusBadge({ status, checking }: { status: CompanyStatus; checking?: boolean }) {
  if (checking) {
    return (
      <Badge tone="dark">
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" /> Checking
      </Badge>
    )
  }
  return <Badge tone={COMPANY_TONE[status]}>{COMPANY_STATUS_LABEL[status]}</Badge>
}

/** "🇮🇳 GCC · L1" for each country the company is listed in. */
export function PresenceBadges({ markets, only }: { markets: CompanyMarket[]; only?: Market }) {
  const shown = only ? markets.filter((m) => m.market === only) : markets
  if (!shown.length) return <span className="text-ink-faint">—</span>
  return (
    <div className="flex flex-wrap gap-1">
      {shown.map((m) => (
        <Badge key={m.market} tone={m.category === 'gcc' ? 'info' : m.category === 'mnc' ? 'dark' : 'neutral'}>
          {MARKET_FLAG[m.market]} {CATEGORY_LABEL[m.category]}
          {m.tier ? ` · L${m.tier}` : ''}
        </Badge>
      ))}
    </div>
  )
}
